import {
  CF_OPENAI_REALTIME_CONFIG,
  generateCFRealtimePrompt,
  getCFInitialMessage,
} from '@/lib/cf-realtime-config';
import { useConversation } from '@elevenlabs/react';
import { useCallback, useRef, useState } from 'react';
import { useVoiceEngine } from './useVoiceEngine';

interface TranscriptEntry {
  source: 'ai' | 'user';
  message: string;
  timestamp: Date;
}

// Track if we've seen the initial AI greeting to filter it from user messages
const initialGreeting = getCFInitialMessage();

interface UseCXConversationOptions {
  engine?: '11labs' | 'realtime';
  onConnect?: () => void;
  onDisconnect?: () => void;
  onMessage?: (data: { source: string; message: string }) => void;
  onError?: (error: any) => void;
  onModeChange?: (data: { mode: string }) => void;
  onTranscriptUpdate?: (entry: TranscriptEntry) => void;
}

export function useCXConversation(options: UseCXConversationOptions = {}) {
  const { engine = '11labs' } = options;
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const hasInitializedRef = useRef(false);
  const sessionIdRef = useRef<string | null>(null);
  const hasSeenInitialGreetingRef = useRef(false);

  // Initialize voice engine for realtime
  const voiceEngine = useVoiceEngine(
    'en',
    engine === 'realtime' ? 'openai' : undefined,
  );

  // ElevenLabs conversation hook
  const conversation = useConversation({
    onConnect: () => {
      console.log('[useCXConversation] Connected to ElevenLabs');
      setIsConnected(true);
      setIsConnecting(false);
      options.onConnect?.();
    },
    onDisconnect: () => {
      console.log('[useCXConversation] Disconnected from ElevenLabs');
      setIsConnected(false);
      setIsConnecting(false);
      setIsSpeaking(false);
      hasInitializedRef.current = false;
      sessionIdRef.current = null;
      hasSeenInitialGreetingRef.current = false; // Reset for next conversation
      options.onDisconnect?.();
    },
    onMessage: (data) => {
      console.log('[useCXConversation] Message received:', data);

      // Update transcript if callback provided
      if (data.message && data.message.trim() && options.onTranscriptUpdate) {
        options.onTranscriptUpdate({
          source: data.source as 'ai' | 'user',
          message: data.message,
          timestamp: new Date(),
        });
      }

      options.onMessage?.(data);
    },
    onError: (error) => {
      console.error('[useCXConversation] Error:', error);
      setIsConnecting(false);
      options.onError?.(error);
    },
    onModeChange: (data) => {
      console.log('[useCXConversation] Mode change:', data);
      setIsSpeaking(data.mode === 'speaking');
      options.onModeChange?.(data);
    },
  });

  const startConversation = useCallback(
    async (agentId: string, firstMessage: string, companyId?: string) => {
      if (hasInitializedRef.current || isConnecting) {
        console.log(
          '[useCXConversation] Already initialized or connecting, skipping',
        );
        return sessionIdRef.current;
      }

      hasInitializedRef.current = true;
      setIsConnecting(true);
      console.log(
        '[useCXConversation] Starting conversation with agent ID:',
        agentId,
      );
      console.log('[useCXConversation] First message:', firstMessage);
      console.log('[useCXConversation] Company ID:', companyId);
      console.log('[useCXConversation] Engine:', engine);

      try {
        if (engine === 'realtime') {
          console.log(
            '[useCXConversation] Starting OpenAI Realtime conversation',
          );

          // Use CF realtime config if company is CF
          const prompt =
            companyId === 'cf'
              ? generateCFRealtimePrompt()
              : 'You are a helpful AI assistant.';
          const initialMessage =
            companyId === 'cf' ? getCFInitialMessage() : firstMessage;
          const voiceId =
            companyId === 'cf' ? CF_OPENAI_REALTIME_CONFIG.voice : 'alloy';

          console.log(
            '[useCXConversation] Starting OpenAI Realtime conversation with prompt:',
            prompt,
          );
          console.log(
            '[useCXConversation] Starting OpenAI Realtime conversation with initial message:',
            initialMessage,
          );
          console.log(
            '[useCXConversation] Starting OpenAI Realtime conversation with voice ID:',
            voiceId,
          );
          const sessionId = await voiceEngine.start(
            {
              language: 'en',
              prompt,
              firstMessage: initialMessage,
              voiceId,
            },
            {
              onConnect: () => {
                console.log('[useCXConversation] Connected to OpenAI Realtime');
                setIsConnected(true);
                setIsConnecting(false);
                options.onConnect?.();
              },
              onError: (error) => {
                console.error(
                  '[useCXConversation] OpenAI Realtime error:',
                  error,
                );
                setIsConnecting(false);
                options.onError?.(error);
              },
              onMessage: (data) => {
                console.log('[useCXConversation] OpenAI message:', data);

                // Filter out initial greeting from appearing as user message
                if (data.message && data.message.trim()) {
                  const isInitialGreeting =
                    data.message.trim() === initialGreeting.trim();

                  // Track that we've seen the initial greeting
                  if (isInitialGreeting && data.source === 'ai') {
                    hasSeenInitialGreetingRef.current = true;
                  }

                  // Skip adding to transcript if:
                  // 1. It's the initial greeting appearing as a user message (bug)
                  // 2. It's the very first message and it's from user (shouldn't happen)
                  const shouldSkip =
                    (isInitialGreeting && data.source === 'user') ||
                    (!hasSeenInitialGreetingRef.current &&
                      data.source === 'user');

                  if (!shouldSkip && options.onTranscriptUpdate) {
                    options.onTranscriptUpdate({
                      source: data.source,
                      message: data.message,
                      timestamp: new Date(),
                    });
                  } else if (shouldSkip) {
                    console.log(
                      '[useCXConversation] Filtered out initial greeting from user:',
                      data.message,
                    );
                  }
                }
                options.onMessage?.(data);
              },
              onAgentSpeaking: (speaking) => {
                setIsSpeaking(speaking);
              },
            },
          );

          sessionIdRef.current = sessionId;
          console.log(
            '[useCXConversation] OpenAI Realtime session started:',
            sessionId,
          );
          return sessionId;
        } else {
          // Use ElevenLabs
          console.log('[useCXConversation] Starting ElevenLabs conversation');
          console.log('[useCXConversation] Agent ID:', agentId);
          console.log('[useCXConversation] First message:', firstMessage);

          const initialMessage =
            companyId === 'cf' ? getCFInitialMessage() : firstMessage;

          const sessionConfig: any = {
            agentId,
            connectionType: 'webrtc',
            overrides: {
              agent: {
                firstMessage: initialMessage,
                prompt: {
                  prompt: generateCFRealtimePrompt(),
                },
              },
            },
          };

          const sessionId = await conversation.startSession(sessionConfig);

          sessionIdRef.current = sessionId;
          console.log(
            '[useCXConversation] ElevenLabs session started:',
            sessionId,
          );
          return sessionId;
        }
      } catch (error) {
        console.error(
          '[useCXConversation] Failed to start conversation:',
          error,
        );
        hasInitializedRef.current = false;
        setIsConnecting(false);
        throw error;
      }
    },
    [engine, conversation, voiceEngine, isConnecting, options],
  );

  const endConversation = useCallback(async () => {
    if (engine === 'realtime') {
      await voiceEngine.stop();
      setIsConnected(false);
      setIsConnecting(false);
      setIsSpeaking(false);
      hasInitializedRef.current = false;
      sessionIdRef.current = null;
      hasSeenInitialGreetingRef.current = false; // Reset for next conversation
      options.onDisconnect?.();
    } else {
      if (conversation.status === 'connected') {
        conversation.endSession();
      }
    }
  }, [engine, conversation, voiceEngine, options]);

  return {
    conversation,
    isConnected:
      engine === 'realtime' ? isConnected : conversation.status === 'connected',
    isConnecting:
      engine === 'realtime'
        ? isConnecting
        : conversation.status === 'connecting',
    isSpeaking: engine === 'realtime' ? isSpeaking : conversation.isSpeaking,
    startConversation,
    endConversation,
    sessionId: sessionIdRef.current,
  };
}
